import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Spinner } from '@components/loading-spinner';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages';
import { TooltipStyles } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { DatePickerStyles } from '@styles/datepicker';

import { useForm, useSaveForm } from 'api/form';

import { DisplayForm } from './display-form';
import { FormLoadError } from './form-load-error';
import { SubmissionSuccess } from './submission-success';

type RootRouteParams = {
  uuid: string;
};

export const ComboSendForm = () => {
  const { t } = useScopedTranslate(translations);
  const { uuid } = useParams() as RootRouteParams;
  const formQuery = useForm(uuid);
  const saveMutation = useSaveForm(uuid);
  const [isFormComplete, setIsFormComplete] = useState<boolean | null>(null);
  const showSubmittingSpinner = useRotatingLoadingMessages({
    title: t('justAMoment'),
    isEnabled: saveMutation.isLoading,
    messages: [isFormComplete ? t('submittingInfo') : t('savingInfo')],
  });

  if (formQuery.isLoading) {
    return <Spinner />;
  }

  if (!formQuery.data) {
    return <FormLoadError />;
  }

  const showSubmissionSuccessPage =
    saveMutation.data?.data.isComplete && saveMutation.isSuccess && !showSubmittingSpinner;

  const handleSubmit = (formData: FormData) => {
    const isSubmit = (formData.get('isComplete') as string) === 'true';
    setIsFormComplete(isSubmit);
    return saveMutation.mutateAsync(formData);
  };

  return (
    <Fragment>
      <DatePickerStyles />
      <TooltipStyles />
      {showSubmissionSuccessPage ? (
        <SubmissionSuccess submissionMessageConfig={formQuery.data?.submissionMessageConfig} />
      ) : (
        <DisplayForm
          key={formQuery.dataUpdatedAt}
          formConfig={formQuery.data.fieldsConfig}
          formAnswers={formQuery.data.formAnswers ?? []}
          onSubmit={handleSubmit}
          hideSubmitSpinner
        />
      )}

      {formQuery.isError && <FormLoadError />}
    </Fragment>
  );
};

const translations = {
  en: {
    justAMoment: 'Just a moment',
    submittingInfo: 'Submitting information...',
    savingInfo: 'Saving information...',
  },
  is: {
    justAMoment: 'Augnablik',
    submittingInfo: 'Sendi gögn...',
    savingInfo: 'Vista gögn...',
  },
};
